import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  CmpEventPaymentStatusEnum,
  useDownloadCmpInvoicePdfMutation,
} from "../../../../graphql/types";
import { commonStyles } from "../../../common/commonStyles";
// POL-005/POL-006 consolidation (B8/B9): display formatting comes from the
// shared dashboard formatters; editable-value unit conversion comes from
// currencyUnits (rounding parity verified — both Math.round, clamp >= 0).
import { formatCurrencyFromCents } from "../../../common/dashboard/formatters";
import {
  centsToCurrencyValue,
  currencyValueToCents,
} from "../../../common/dashboard/currencyUnits";
import { CurrencyInput } from "../../../common/dashboard/CurrencyInput";
import { SectionShell } from "../../../common/dashboard/SectionShell";
import {
  ClearFiltersButton,
  FilterBar,
  FilterMenuButton,
  FilterSearchField,
  clearFiltersButtonSx,
  filterFieldSx,
} from "../../../common/dashboard/FilterBar";
import { InvoiceSummaryCard } from "../../Invoicing/InvoiceMockComponents";
import { downloadBase64File } from "../../Invoicing/invoiceFileDownloads";

// Re-exported for the existing consumer fan-out; canonical values live in
// commonStyles (POL-006 token consolidation).
export const cpaGold = commonStyles.cpaGold;
export const cpaGoldDark = commonStyles.cpaGoldDark;

// ---------------------------------------------------------------------------
// Shared button / field sx (B9 plan 10 bounded absorption — both sales pages
// consume these; values are B8's page-local constants promoted verbatim)
// ---------------------------------------------------------------------------

export const goldButtonSx = {
  backgroundColor: cpaGold,
  color: "white",
  fontWeight: 700,
  textTransform: "none",
  "&:hover": { backgroundColor: cpaGoldDark },
} as const;

export const blackButtonSx = {
  backgroundColor: "#111827",
  color: "white",
  textTransform: "none",
  "&:hover": { backgroundColor: "#000000" },
} as const;

export const redButtonSx = {
  backgroundColor: "#F31723",
  color: "white",
  textTransform: "none",
  "&:hover": { backgroundColor: "#D90F1A" },
} as const;

// Gold-focus editable field + grey disabled fill (mock tokens: #8F969E
// placeholder grey, #F3F4F6 disabled fill).
export const goldFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    "&.Mui-focused fieldset": {
      borderColor: cpaGold,
      borderWidth: 1,
    },
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#8F969E",
    opacity: 1,
  },
} as const;

export const disabledFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#F3F4F6",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#8F969E",
    opacity: 1,
  },
} as const;

// ---------------------------------------------------------------------------
// Currency + value helpers (cents are the backend source of truth)
// ---------------------------------------------------------------------------

// Legacy names kept for the existing consumer fan-out; the implementations
// now live in common/dashboard/currencyUnits (they were promoted verbatim
// from here, so behavior is identical).
export const dollarsToCents = currencyValueToCents;
export const centsToDollarsString = centsToCurrencyValue;

export { formatCurrencyFromCents };

type InvoiceTaxCategorySettingsForDisplay = {
  enabled?: boolean | null;
  firstTaxRateLabel?: string | null;
  firstTaxRatePercentage?: number | null;
  secondTaxRatePercentage?: number | null;
};

const getPositiveTaxRatePercentage = (value: number | null | undefined) =>
  Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : 0;

export const getConfiguredInvoiceTaxRatePercentage = (
  taxSettings?: InvoiceTaxCategorySettingsForDisplay | null
) => {
  if (!taxSettings?.enabled) {
    return 0;
  }

  return (
    getPositiveTaxRatePercentage(taxSettings.firstTaxRatePercentage) +
    getPositiveTaxRatePercentage(taxSettings.secondTaxRatePercentage)
  );
};

export const getConfiguredInvoiceTaxRateLabel = (
  taxSettings?: InvoiceTaxCategorySettingsForDisplay | null
) => taxSettings?.firstTaxRateLabel?.trim() || "Tax";

export const splitName = (
  fullName: string
): { first: string; last: string } => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    return { first: parts[0] || "", last: "" };
  }
  return { first: parts[0], last: parts.slice(1).join(" ") };
};

export const isComplimentaryEventPaymentStatus = (
  status: CmpEventPaymentStatusEnum
): boolean =>
  status === CmpEventPaymentStatusEnum.NotRequired ||
  status === CmpEventPaymentStatusEnum.Waived;

export const isPaidEventPaymentStatus = (
  status: CmpEventPaymentStatusEnum
): boolean =>
  status === CmpEventPaymentStatusEnum.Paid ||
  isComplimentaryEventPaymentStatus(status);

// Shared "Print/Download Invoice PDF" action (reuses the Invoicing backend).
export const useDownloadEventInvoicePdf = (chamberId: string) => {
  const [downloadPdf, { loading: downloadingPdf }] =
    useDownloadCmpInvoicePdfMutation();

  const downloadInvoicePdf = async (invoiceId?: string | null) => {
    if (!invoiceId) {
      return;
    }
    const result = await downloadPdf({
      variables: { input: { chamberId, invoiceId } },
    });
    const download = result.data?.downloadCmpInvoicePdf.download;
    if (download) {
      downloadBase64File({
        base64Data: download.base64Data,
        contentType: download.contentType,
        filename: download.filename,
      });
    }
  };

  return { downloadInvoicePdf, downloadingPdf };
};

// ---------------------------------------------------------------------------
// Shared status chip
// ---------------------------------------------------------------------------

export type ChipTone =
  | "green"
  | "amber"
  | "red"
  | "blue"
  | "gray"
  | "purple"
  | "sky";

const chipToneClassName: Record<ChipTone, string> = {
  green: "bg-green-100 text-green-800",
  amber: "bg-amber-100 text-amber-800",
  red: "bg-red-100 text-red-800",
  blue: "bg-blue-100 text-blue-800",
  gray: "bg-gray-100 text-gray-700",
  purple: "bg-purple-100 text-purple-800",
  sky: "bg-sky-100 text-sky-800",
};

export const EventStatusChip = ({
  label,
  tone,
}: {
  label: string;
  tone: ChipTone;
}) => (
  <Chip
    label={label}
    size="small"
    className={`${chipToneClassName[tone]} font-semibold`}
  />
);

// ---------------------------------------------------------------------------
// Page section wrapper — facade over the shared SectionShell (B5
// InvoiceSection precedent): both sales pages render the platform section
// skin (black-border card + gold title pill) in one change. Titles and
// descriptions stay verbatim; description is a ReactNode so the manage-list
// sections can carry their bold tail.
// ---------------------------------------------------------------------------

export const EventSalesSection = ({
  children,
  id,
  title,
  description,
}: {
  children: React.ReactNode;
  id: string;
  title: string;
  description?: React.ReactNode;
}) => (
  <SectionShell id={id} title={title} description={description}>
    {children}
  </SectionShell>
);

// ---------------------------------------------------------------------------
// Filter bar (B8's 56px search + Button/Menu filter design, built on the
// shared common/dashboard/FilterBar primitives; B7 ManageEvents precedent for
// the sx escape hatches — width:auto per instance, idle label #8F969E)
// ---------------------------------------------------------------------------

export type EventSalesFilterMenuConfig = {
  label: string;
  active: boolean;
  onOpen: (anchorEl: HTMLElement) => void;
};

// The popup <Menu>s stay page-owned; this is the row layout, search field and
// trigger/clear buttons only (same split as the common FilterBar).
export const EventSalesFilterBar = ({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  filters,
  onClearAllFilters,
}: {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  filters: EventSalesFilterMenuConfig[];
  onClearAllFilters: () => void;
}) => (
  <FilterBar className="flex flex-wrap items-center gap-3">
    <FilterSearchField
      value={searchValue}
      onChange={onSearchChange}
      placeholder={searchPlaceholder}
      className=""
      fullWidth={false}
      fieldSx={[{ flex: 1, minWidth: 280 }, filterFieldSx]}
    />
    {filters.map((filter) => (
      <FilterMenuButton
        key={filter.label}
        label={filter.label}
        onOpen={filter.onOpen}
        active={filter.active}
        sx={
          filter.active
            ? { width: "auto" }
            : { width: "auto", color: "#8F969E" }
        }
      />
    ))}
    {/* POL-013 (Q-026 answered: plural platform-wide). */}
    <ClearFiltersButton
      onClick={onClearAllFilters}
      sx={[clearFiltersButtonSx, { px: 3, width: "auto" }]}
    />
  </FilterBar>
);

// ---------------------------------------------------------------------------
// Manage-list sortable headers (functional asc/desc toggle; B8's
// implementation promoted so B9's decorative affordances become real)
// ---------------------------------------------------------------------------

export type EventSalesSortState<Column extends string> = {
  column: Column;
  direction: "asc" | "desc";
};

export function toggleEventSalesSort<Column extends string>(
  current: EventSalesSortState<Column> | null,
  column: Column
): EventSalesSortState<Column> | null {
  if (current?.column !== column) {
    return { column, direction: "asc" };
  }
  if (current.direction === "asc") {
    return { column, direction: "desc" };
  }
  return null;
}

export function EventSalesSortableHeader<Column extends string>({
  label,
  column,
  sort,
  onToggle,
}: {
  label: string;
  column: Column;
  sort: EventSalesSortState<Column> | null;
  onToggle: (column: Column) => void;
}): React.ReactElement {
  const isActive = sort?.column === column;
  return (
    <button
      type="button"
      onClick={() => onToggle(column)}
      className="inline-flex items-center gap-1 whitespace-nowrap font-bold text-gray-950"
      aria-label={`Sort by ${label}`}
    >
      {label}
      <span className="flex flex-col" aria-hidden>
        <ArrowDropUpIcon
          sx={{
            color: isActive && sort?.direction === "asc" ? "#111827" : "#9CA3AF",
            fontSize: 18,
            mb: "-11px",
          }}
        />
        <ArrowDropDownIcon
          sx={{
            color:
              isActive && sort?.direction === "desc" ? "#111827" : "#9CA3AF",
            fontSize: 18,
          }}
        />
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// CSV download helper (identical blob-anchor dance on both sales pages)
// ---------------------------------------------------------------------------

export const downloadCsvFile = ({
  csvContent,
  contentType,
  filename,
}: {
  csvContent: string;
  contentType: string;
  filename: string;
}) => {
  const blob = new Blob([csvContent], { type: contentType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------------------
// Create-invoice line-item table chrome (blue 7-column header, shared by the
// ticket + sponsorship create forms; the single editable row is page-owned)
// ---------------------------------------------------------------------------

const invoiceLineItemHeadings = [
  "Code",
  "Description",
  "Qty",
  "Account #",
  "Unit Price",
  "Tax",
  "Total",
];

export const InvoiceLineItemTable = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <TableContainer className="rounded border border-gray-200">
    <Table size="small">
      <TableHead className="bg-[#2f7de1]">
        <TableRow>
          {invoiceLineItemHeadings.map((heading) => (
            <TableCell
              key={heading}
              className="!font-bold !uppercase !text-white"
            >
              {heading}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>{children}</TableRow>
      </TableBody>
    </Table>
  </TableContainer>
);

// Red square trash button on the line-item row (mock token #F31723).
export const LineItemDeleteButton = ({
  onClick,
  ariaLabel = "delete line item",
}: {
  onClick: () => void;
  ariaLabel?: string;
}) => (
  <IconButton
    size="small"
    aria-label={ariaLabel}
    onClick={onClick}
    sx={{
      backgroundColor: "#F31723",
      borderRadius: "6px",
      color: "white",
      "&:hover": { backgroundColor: "#D90F1A" },
    }}
  >
    <DeleteOutlineIcon fontSize="small" />
  </IconButton>
);

// ---------------------------------------------------------------------------
// Analytics card row
// ---------------------------------------------------------------------------

export type EventStatCard = {
  label: string;
  value: string;
  // Small sub-value rendered INSIDE the card (mock: green "40/100" line).
  helper?: string;
  icon?: React.ReactNode;
  // Small bold footnote rendered BELOW the card row, under the card it
  // qualifies (mock: "*Includes unpaid , sponsorship and complimentary").
  footnote?: string;
};

const statRowXlColsClassName: Record<number, string> = {
  3: "xl:grid-cols-3",
  4: "xl:grid-cols-4",
  5: "xl:grid-cols-5",
  6: "xl:grid-cols-6",
};

// hideIcons renders the mock's text-only analytics cards (EVT-SALES-T-001 /
// S-001): bold grey label + large bold value, no icon anywhere. The default
// (false) keeps the Invoicing-module InvoiceSummaryCard rendering untouched.
export const EventStatCardRow = ({
  cards,
  hideIcons = false,
}: {
  cards: EventStatCard[];
  hideIcons?: boolean;
}) => {
  const gridClassName = `grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 ${
    statRowXlColsClassName[cards.length] || "xl:grid-cols-6"
  }`;

  if (!hideIcons) {
    return (
      <div className={gridClassName}>
        {cards.map((card) => (
          <InvoiceSummaryCard
            key={card.label}
            label={card.label}
            value={card.value}
            helper={card.helper}
            icon={card.icon}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={gridClassName}>
      {cards.map((card) => (
        <div key={card.label} className="flex flex-col">
          <div className="flex-1 rounded border border-gray-200 bg-white p-4">
            <p className="text-sm font-bold text-gray-600">{card.label}</p>
            <p className="mt-1 text-2xl font-bold text-gray-950">
              {card.value}
            </p>
            {card.helper ? (
              // Mock shows the in-card sub-value as small GREEN text (40/100).
              <p className="mt-1 text-xs font-semibold text-green-600">
                {card.helper}
              </p>
            ) : null}
          </div>
          {card.footnote ? (
            <p className="mt-1 text-xs font-bold text-gray-950">
              {card.footnote}
            </p>
          ) : null}
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Event select dropdown
// ---------------------------------------------------------------------------

export type EventSelectOption = {
  id: string;
  title: string;
  startAtUtcMilli: number;
  status: string;
};

// No floating MUI label (EVT-SALES-T-004): optional static bold label above
// the control + grey "Select Event" placeholder text. Gold focus comes from
// the shared theme.
export const EventSelect = ({
  options,
  value,
  onChange,
  loading,
  label = "",
}: {
  options: EventSelectOption[];
  value: string;
  onChange: (eventId: string) => void;
  loading?: boolean;
  label?: string;
}) => (
  <div>
    {label ? (
      <p className="mb-1 text-sm font-bold text-gray-950">{label}</p>
    ) : null}
    <TextField
      select
      fullWidth
      value={value}
      disabled={loading}
      onChange={({ target }) => onChange(target.value)}
      SelectProps={{
        displayEmpty: true,
        renderValue: (selected) => {
          const selectedId = String(selected || "");
          const option = options.find((item) => item.id === selectedId);
          if (option) {
            return option.title;
          }
          return (
            <span className="text-gray-500">
              {loading ? "Loading events..." : "Select Event"}
            </span>
          );
        },
      }}
    >
      <MenuItem value="" disabled>
        {loading
          ? "Loading events..."
          : options.length === 0
            ? "No events found"
            : "Select Event"}
      </MenuItem>
      {options.map((option) => (
        <MenuItem value={option.id} key={option.id}>
          {option.title}
        </MenuItem>
      ))}
    </TextField>
  </div>
);

// ---------------------------------------------------------------------------
// Member company + billing selector (shared by both invoice pages)
// ---------------------------------------------------------------------------

export type BillingDetails = {
  selectedMemberId: string;
  customerName: string;
  streetAddress: string;
  city: string;
  postalCode: string;
  billingContactName: string;
  billingEmail: string;
  billingPhone: string;
};

export const blankBillingDetails = (): BillingDetails => ({
  selectedMemberId: "",
  customerName: "",
  streetAddress: "",
  city: "",
  postalCode: "",
  billingContactName: "",
  billingEmail: "",
  billingPhone: "",
});

export type MemberLookupOption = {
  id: string;
  chamberMemberId?: string | null;
  label: string;
  billingSnapshot: {
    customerName: string;
    contactName?: string | null;
    contactEmail: string;
    contactPhone?: string | null;
    addressLine1?: string | null;
    city?: string | null;
    postalCode?: string | null;
  };
};

export const billingFromMemberOption = (
  option: MemberLookupOption
): BillingDetails => ({
  selectedMemberId: option.id,
  customerName: option.billingSnapshot.customerName,
  streetAddress: option.billingSnapshot.addressLine1 || "",
  city: option.billingSnapshot.city || "",
  postalCode: option.billingSnapshot.postalCode || "",
  billingContactName:
    option.billingSnapshot.contactName || option.billingSnapshot.customerName,
  billingEmail: option.billingSnapshot.contactEmail,
  billingPhone: option.billingSnapshot.contactPhone || "",
});

export type MemberBillingMode = "member" | "non-member";

// ONE bordered/grouped card per the mock (EVT-SALES-S-002): card heading +
// helper + member dropdown + billing address + contact fields all inside the
// same card border. Required asterisks are RED.
//
// - `mode`/`onModeChange`: member vs non-member purchaser toggle
//   (EVT-BLAIR-SALES-001), styled like module-5 CreateInvoice's segmented
//   "Existing member / Non-member" toggle. Non-member mode hides the member
//   dropdown and shows an editable Customer / Company Name field; billing
//   address/contact/email/phone stay editable in both modes. The toggle only
//   renders when `onModeChange` is provided.
// - `onSearchChange`: debounced server-side member search
//   (EVT-BLAIR-SALES-002) — the page owns the query and passes `members`.
export const MemberBillingSelector = ({
  members,
  billing,
  onChange,
  loading,
  mode = "member",
  onModeChange,
  onSearchChange,
  heading = "Select Member Company To Invoice",
  helperText = "Edit member contact, email and phone number, if different than what auto-populates. Member Logo and website URL will be populated from member profile",
}: {
  members: MemberLookupOption[];
  billing: BillingDetails;
  onChange: (next: BillingDetails) => void;
  loading?: boolean;
  mode?: MemberBillingMode;
  onModeChange?: (mode: MemberBillingMode) => void;
  onSearchChange?: (search: string) => void;
  heading?: string;
  helperText?: string;
}) => {
  const update = (patch: Partial<BillingDetails>) =>
    onChange({ ...billing, ...patch });

  // Keep the selected member renderable even while a new server-side search
  // narrows `members` to a list that no longer contains it.
  const [selectedOption, setSelectedOption] =
    useState<MemberLookupOption | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    },
    []
  );

  const selectedMember =
    members.find((member) => member.id === billing.selectedMemberId) ||
    (selectedOption && selectedOption.id === billing.selectedMemberId
      ? selectedOption
      : null);
  const memberOptions =
    selectedMember && !members.some((member) => member.id === selectedMember.id)
      ? [selectedMember, ...members]
      : members;

  const handleSearchInput = (search: string) => {
    if (!onSearchChange) {
      return;
    }
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(
      () => onSearchChange(search.trim()),
      350
    );
  };

  return (
    <div className="rounded border border-gray-200 bg-white p-4">
      <h3 className="text-base font-bold text-gray-950">{heading}</h3>
      <p className="mt-1 text-sm text-gray-600">{helperText}</p>

      <div className="mt-4 space-y-4">
        {onModeChange ? (
          <div>
            <p className="text-sm font-bold text-gray-950">
              Bill To <span className="text-red-500">*</span>
            </p>
            <p className="mb-2 text-xs text-gray-500">
              Invoice an existing member or a one-off, non-member customer.
            </p>
            <div className="inline-flex rounded-md border border-gray-300 p-1">
              {(
                [
                  { value: "member", label: "Existing member" },
                  {
                    value: "non-member",
                    label: "Non-member / one-off customer",
                  },
                ] as const
              ).map((option) => {
                const isActive = mode === option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => onModeChange(option.value)}
                    aria-pressed={isActive}
                    className={`rounded px-4 py-2 text-sm font-bold transition ${
                      isActive
                        ? "bg-cpaGold text-white"
                        : "bg-transparent text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {mode === "member" ? (
          <div>
            <p className="text-sm font-bold text-gray-950">
              Member <span className="text-red-500">*</span>
            </p>
            <p className="mb-1 text-xs text-gray-500">
              Select the member from the drop down.
            </p>
            <Autocomplete
              options={memberOptions}
              value={selectedMember}
              loading={loading}
              fullWidth
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, selected) =>
                option.id === selected.id
              }
              // Server-side search owns filtering when wired up.
              filterOptions={
                onSearchChange
                  ? (options: MemberLookupOption[]) => options
                  : undefined
              }
              onChange={(_, option) => {
                setSelectedOption(option);
                if (option) {
                  onChange(billingFromMemberOption(option));
                } else {
                  update({ selectedMemberId: "" });
                }
              }}
              onInputChange={(_, inputValue, reason) => {
                if (reason === "input") {
                  handleSearchInput(inputValue);
                }
              }}
              loadingText="Loading members..."
              noOptionsText={loading ? "Loading members..." : "No members found"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Member Name"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </div>
        ) : (
          <div>
            <p className="text-sm font-bold text-gray-950">
              Customer / Company Name <span className="text-red-500">*</span>
            </p>
            <p className="mb-1 text-xs text-gray-500">
              Enter the customer or company being invoiced.
            </p>
            <TextField
              placeholder="Enter Customer or Company Name"
              value={billing.customerName}
              onChange={({ target }) => update({ customerName: target.value })}
              fullWidth
            />
          </div>
        )}

        <div>
          <p className="text-sm font-bold text-gray-950">
            Billing Address <span className="text-red-500">*</span>
          </p>
          <p className="mb-1 text-xs text-gray-500">Edit if not shown correctly</p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <TextField
              placeholder="Enter Street Number and Name"
              value={billing.streetAddress}
              onChange={({ target }) => update({ streetAddress: target.value })}
              fullWidth
            />
            <TextField
              placeholder="Enter City Name"
              value={billing.city}
              onChange={({ target }) => update({ city: target.value })}
              fullWidth
            />
            <TextField
              placeholder="Enter Postal Code"
              value={billing.postalCode}
              onChange={({ target }) => update({ postalCode: target.value })}
              fullWidth
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div>
            <p className="text-sm font-bold text-gray-950">
              Billing Contact Name <span className="text-red-500">*</span>
            </p>
            <TextField
              placeholder="Enter Contact Name"
              value={billing.billingContactName}
              onChange={({ target }) =>
                update({ billingContactName: target.value })
              }
              fullWidth
              className="mt-1"
            />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-950">
              Email <span className="text-red-500">*</span>
            </p>
            <TextField
              placeholder="Enter Contact Email"
              value={billing.billingEmail}
              onChange={({ target }) => update({ billingEmail: target.value })}
              fullWidth
              className="mt-1"
            />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-950">
              Phone <span className="text-red-500">*</span>
            </p>
            <TextField
              placeholder="Enter Phone Number"
              value={billing.billingPhone}
              onChange={({ target }) => update({ billingPhone: target.value })}
              fullWidth
              className="mt-1"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Attendee assignment rows (Name / Email / Phone)
// ---------------------------------------------------------------------------

export type AttendeeAssignment = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

let attendeeIdCounter = 0;
export const blankAttendee = (): AttendeeAssignment => {
  attendeeIdCounter += 1;
  return {
    id: `attendee-${attendeeIdCounter}`,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  };
};

// `attendeeFieldsOptional` keeps email optional for admin sales pages.
// `attendeeNameRequired` supports package tickets where every included seat
// must still have an attendee record.
export const AttendeeAssignmentList = ({
  attendees,
  onChange,
  helperText,
  attendeeFieldsOptional = false,
  attendeeNameRequired,
}: {
  attendees: AttendeeAssignment[];
  onChange: (next: AttendeeAssignment[]) => void;
  helperText?: string;
  attendeeFieldsOptional?: boolean;
  attendeeNameRequired?: boolean;
}) => {
  const update = (id: string, patch: Partial<AttendeeAssignment>) =>
    onChange(
      attendees.map((attendee) =>
        attendee.id === id ? { ...attendee, ...patch } : attendee
      )
    );

  const nameRequiredMark =
    attendeeNameRequired || !attendeeFieldsOptional ? (
      <span className="text-red-500"> *</span>
    ) : null;
  const emailRequiredMark = attendeeFieldsOptional ? null : (
    <span className="text-red-500"> *</span>
  );

  return (
    <div className="space-y-3">
      {helperText ? (
        <p className="text-sm text-gray-600">{helperText}</p>
      ) : null}
      <div className="hidden grid-cols-[28px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-3 px-1 text-xs font-bold uppercase text-gray-500 md:grid">
        <span>#</span>
        <span>Attendee Name{nameRequiredMark}</span>
        <span>Attendee Email{emailRequiredMark}</span>
        <span>Attendee Phone Number</span>
      </div>
      {attendees.map((attendee, index) => (
        <div
          key={attendee.id}
          className="grid grid-cols-1 items-center gap-3 md:grid-cols-[28px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]"
        >
          <span className="text-sm font-semibold text-gray-500">{index + 1}</span>
          <div className="grid grid-cols-2 gap-2">
            <TextField
              size="small"
              placeholder="First Name"
              value={attendee.firstName}
              onChange={({ target }) =>
                update(attendee.id, { firstName: target.value })
              }
              fullWidth
            />
            <TextField
              size="small"
              placeholder="Last Name"
              value={attendee.lastName}
              onChange={({ target }) =>
                update(attendee.id, { lastName: target.value })
              }
              fullWidth
            />
          </div>
          <TextField
            size="small"
            placeholder="attendee@email.com"
            value={attendee.email}
            onChange={({ target }) =>
              update(attendee.id, { email: target.value })
            }
            fullWidth
          />
          <TextField
            size="small"
            placeholder="Phone Number"
            value={attendee.phone}
            onChange={({ target }) =>
              update(attendee.id, { phone: target.value })
            }
            fullWidth
          />
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Confirm dialog ("Are you sure ...")
// ---------------------------------------------------------------------------

// Mock confirm pattern: red circular X close, large RED circle with white
// question mark, bold centered copy, PROCEED/CANCEL style buttons.
// `confirmTone="blue"` gives the payment modals their blue PROCEED button.
export const ConfirmDialog = ({
  open,
  tone = "warning",
  title,
  message,
  confirmLabel = "Yes",
  cancelLabel = "Cancel",
  confirmTone = "gold",
  loading,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  tone?: "warning" | "danger" | "question";
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmTone?: "gold" | "blue";
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  const toneConfig = {
    warning: { bg: "bg-amber-500", icon: <WarningAmberIcon fontSize="large" /> },
    // Mock's "red-?" confirm pattern for destructive + confirm-payment flows.
    danger: { bg: "bg-red-500", icon: <HelpOutlineIcon fontSize="large" /> },
    question: { bg: "bg-red-500", icon: <HelpOutlineIcon fontSize="large" /> },
  }[tone];
  const confirmSx =
    confirmTone === "blue"
      ? {
          backgroundColor: "#2563eb",
          "&:hover": { backgroundColor: "#1d4ed8" },
        }
      : {
          backgroundColor: cpaGold,
          "&:hover": { backgroundColor: cpaGoldDark },
        };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogContent className="relative">
        <IconButton
          size="small"
          onClick={onCancel}
          className="!absolute right-2 top-2"
          sx={{
            backgroundColor: "#ef4444",
            color: "#ffffff",
            "&:hover": { backgroundColor: "#dc2626" },
          }}
          aria-label="close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <div className="flex flex-col items-center px-2 py-4 text-center">
          <div
            className={`mb-4 flex h-16 w-16 items-center justify-center rounded-full ${toneConfig.bg} text-white`}
          >
            {toneConfig.icon}
          </div>
          <h3 className="text-xl font-bold text-gray-950">{title}</h3>
          {message ? (
            <p className="mt-2 text-sm text-gray-600">{message}</p>
          ) : null}
          <div className="mt-6 flex w-full gap-3">
            <Button
              fullWidth
              variant="contained"
              disabled={loading}
              onClick={onConfirm}
              startIcon={
                loading ? (
                  <CircularProgress color="inherit" size={16} />
                ) : undefined
              }
              sx={confirmSx}
            >
              {confirmLabel}
            </Button>
            <Button
              fullWidth
              variant="contained"
              disabled={loading}
              onClick={onCancel}
              sx={{
                backgroundColor: "#111827",
                "&:hover": { backgroundColor: "#000000" },
              }}
            >
              {cancelLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Result modal (success / failure)
// ---------------------------------------------------------------------------

export const ResultModal = ({
  open,
  success = true,
  title,
  message,
  onClose,
}: {
  open: boolean;
  success?: boolean;
  title: string;
  message?: string;
  onClose: () => void;
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogContent className="relative">
      {/* Mock success/failure pop-ups close with a RED circular X. */}
      <IconButton
        size="small"
        onClick={onClose}
        className="!absolute right-2 top-2"
        sx={{
          backgroundColor: "#ef4444",
          color: "#ffffff",
          "&:hover": { backgroundColor: "#dc2626" },
        }}
        aria-label="close"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <div className="flex flex-col items-center px-2 py-6 text-center">
        <div
          className={`mb-4 flex h-16 w-16 items-center justify-center rounded-full ${
            success ? "bg-green-500" : "bg-red-500"
          } text-white`}
        >
          {success ? (
            <CheckCircleIcon fontSize="large" />
          ) : (
            <WarningAmberIcon fontSize="large" />
          )}
        </div>
        <h3 className="text-xl font-bold text-gray-950">{title}</h3>
        {message ? (
          <p className="mt-2 text-sm text-gray-600">{message}</p>
        ) : null}
      </div>
    </DialogContent>
  </Dialog>
);

// ---------------------------------------------------------------------------
// Update Attendee Names modal (shared by both sales pages; the two visual
// variants preserve each page's existing DOM verbatim — "labeled" is the
// ticket page's "Attendee {n}" rows, "numbered" is the sponsorship page's
// index-column rows)
// ---------------------------------------------------------------------------

export type EditableAttendeeName = {
  id: string;
  firstName: string;
  lastName: string;
};

export const UpdateAttendeeNamesModal = ({
  open,
  title,
  attendees,
  onAttendeeChange,
  onClose,
  onSave,
  saving,
  emptyMessage,
  variant = "labeled",
  fieldSx,
}: {
  open: boolean;
  title: string;
  attendees: EditableAttendeeName[];
  onAttendeeChange: (
    id: string,
    patch: { firstName?: string; lastName?: string }
  ) => void;
  onClose: () => void;
  onSave: () => void;
  saving?: boolean;
  emptyMessage?: string;
  variant?: "labeled" | "numbered";
  fieldSx?: object;
}) => {
  const nameFields = (attendee: EditableAttendeeName) => (
    <>
      <TextField
        size="small"
        placeholder="First Name"
        value={attendee.firstName}
        onChange={({ target }) =>
          onAttendeeChange(attendee.id, { firstName: target.value })
        }
        fullWidth
        sx={fieldSx}
      />
      <TextField
        size="small"
        placeholder="Last Name"
        value={attendee.lastName}
        onChange={({ target }) =>
          onAttendeeChange(attendee.id, { lastName: target.value })
        }
        fullWidth
        sx={fieldSx}
      />
    </>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent className="relative">
        <IconButton
          size="small"
          onClick={onClose}
          className="!absolute right-2 top-2 text-gray-400"
          aria-label="close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <h3 className="mb-4 text-lg font-bold text-gray-950">{title}</h3>
        {attendees.length === 0 && emptyMessage ? (
          <p className="text-sm text-gray-500">{emptyMessage}</p>
        ) : null}
        <div className={variant === "labeled" ? "space-y-4" : "space-y-3"}>
          {attendees.map((attendee, index) =>
            variant === "labeled" ? (
              <div key={attendee.id}>
                <p className="mb-1 text-sm font-bold text-gray-950">
                  Attendee {index + 1}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {nameFields(attendee)}
                </div>
              </div>
            ) : (
              <div
                key={attendee.id}
                className="grid grid-cols-[28px_minmax(0,1fr)_minmax(0,1fr)] items-center gap-2"
              >
                <span className="text-sm font-semibold text-gray-500">
                  {index + 1}
                </span>
                {nameFields(attendee)}
              </div>
            )
          )}
        </div>
        <div className="mt-5 flex justify-end gap-3">
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={onSave}
            sx={goldButtonSx}
          >
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Process Payment modal (Open Invoice -> record manual payment)
// ---------------------------------------------------------------------------

export type ManualPaymentMethodOption = {
  id: string;
  label: string;
  enabled: boolean;
};

export type ProcessPaymentSubmit = {
  amountInCents: number;
  manualPaymentMethodId: string;
  receivedAtUtcMilli: number;
  reference: string;
  note: string;
};

export type EventPaymentInvoiceLineItem = {
  id?: string;
  itemCode?: string | null;
  description: string;
  quantity: number;
  unitPriceInCents: number;
  subtotalAmountInCents?: number | null;
  taxAmountInCents?: number | null;
  totalAmountInCents: number;
};

const todayDateInput = () => new Date().toISOString().slice(0, 10);

// POL-005 modal skin for the shared CurrencyInput: 56px height / 4px radius /
// 16px text so the Amount to Pay field matches the modal's outlined MUI
// TextFields (wizardCurrencyInputClassName precedent).
const paymentModalCurrencyInputClassName =
  "h-14 w-full rounded border border-gray-300 bg-white pl-7 pr-3 text-base text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-cpaGold focus:ring-2 focus:ring-amber-100";

export const ProcessPaymentModal = ({
  open,
  customerName,
  invoiceNumber,
  amountDueInCents,
  invoiceLineItems = [],
  subtotalAmountInCents,
  taxAmountInCents,
  totalAmountInCents,
  paymentMethods,
  loading,
  onSubmit,
  onClose,
  onPrintPdf,
  printLoading,
}: {
  open: boolean;
  customerName: string;
  invoiceNumber: string;
  amountDueInCents: number;
  invoiceLineItems?: EventPaymentInvoiceLineItem[];
  subtotalAmountInCents?: number | null;
  taxAmountInCents?: number | null;
  totalAmountInCents?: number | null;
  paymentMethods: ManualPaymentMethodOption[];
  loading?: boolean;
  onSubmit: (payload: ProcessPaymentSubmit) => void;
  onClose: () => void;
  // Mock's blue "Print PDF" button (top-right). Optional for backward compat.
  onPrintPdf?: () => void;
  printLoading?: boolean;
}) => {
  const enabledMethods = useMemo(
    () => paymentMethods.filter((method) => method.enabled),
    [paymentMethods]
  );
  const [methodId, setMethodId] = useState("");
  const [paymentDate, setPaymentDate] = useState(todayDateInput());
  const [amount, setAmount] = useState(centsToDollarsString(amountDueInCents));
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const subtotal =
    subtotalAmountInCents ??
    invoiceLineItems.reduce(
      (sum, item) => sum + (item.subtotalAmountInCents ?? item.unitPriceInCents * item.quantity),
      0
    );
  const tax =
    taxAmountInCents ??
    invoiceLineItems.reduce((sum, item) => sum + (item.taxAmountInCents ?? 0), 0);
  const total =
    totalAmountInCents ??
    invoiceLineItems.reduce((sum, item) => sum + item.totalAmountInCents, 0);

  useEffect(() => {
    if (open) {
      setMethodId(enabledMethods[0]?.id || "");
      setPaymentDate(todayDateInput());
      setAmount(centsToDollarsString(amountDueInCents));
      setReference("");
      setNote("");
      setConfirmOpen(false);
    }
  }, [open, amountDueInCents, enabledMethods]);

  const handleConfirm = () => {
    onSubmit({
      amountInCents: dollarsToCents(amount),
      manualPaymentMethodId: methodId,
      receivedAtUtcMilli: new Date(`${paymentDate}T12:00:00`).getTime(),
      reference,
      note,
    });
    setConfirmOpen(false);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent className="relative">
          {/* Plain dark X close (mock); the red circular X is for pop-ups. */}
          <IconButton
            size="small"
            onClick={onClose}
            className="!absolute right-2 top-2 text-gray-600"
            aria-label="close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <div className="flex items-start justify-between gap-3 pr-8">
            <div>
              <h3 className="text-lg font-bold text-gray-950">
                Process Payment
              </h3>
              <p className="mt-2 text-sm font-bold text-gray-900">
                {customerName}
              </p>
            </div>
            {onPrintPdf ? (
              <Button
                variant="contained"
                onClick={onPrintPdf}
                disabled={printLoading}
                startIcon={
                  printLoading ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : (
                    <PrintOutlinedIcon />
                  )
                }
                sx={{
                  backgroundColor: "#2563eb",
                  "&:hover": { backgroundColor: "#1d4ed8" },
                  textTransform: "none",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                Print PDF
              </Button>
            ) : null}
          </div>

          <hr className="my-3 border-gray-200" />

          <p className="text-sm font-bold text-gray-950">Reference Invoice</p>
          <p className="text-lg font-bold text-gray-950">{invoiceNumber}</p>

          <p className="mt-4 text-xs font-bold uppercase text-gray-700">
            Remaining Balance Owing
          </p>
          <div className="mt-1 rounded border border-gray-900 bg-white px-3 py-2 text-xl font-bold text-blue-600">
            {formatCurrencyFromCents(amountDueInCents)}
          </div>

          {/* The mock's Process Payment modal carries no line-item table; it
              only renders when the page passes invoice line items. */}
          {invoiceLineItems.length > 0 ? (
            <>
              <div className="mt-4 overflow-hidden rounded border border-gray-200">
                <div className="grid grid-cols-[88px_minmax(0,1fr)_72px_110px_110px] bg-blue-600 px-3 py-2 text-xs font-bold uppercase text-white">
                  <span>Code</span>
                  <span>Description</span>
                  <span className="text-center">Qty</span>
                  <span className="text-right">Unit Price</span>
                  <span className="text-right">Total</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {invoiceLineItems.map((item) => (
                    <div
                      key={
                        item.id || `${item.description}-${item.itemCode || "item"}`
                      }
                      className="grid grid-cols-[88px_minmax(0,1fr)_72px_110px_110px] items-center px-3 py-3 text-sm"
                    >
                      <span className="font-semibold text-gray-900">
                        {item.itemCode || "—"}
                      </span>
                      <span className="text-gray-800">{item.description}</span>
                      <span className="text-center text-gray-700">
                        {item.quantity}
                      </span>
                      <span className="text-right text-gray-700">
                        {formatCurrencyFromCents(item.unitPriceInCents)}
                      </span>
                      <span className="text-right font-semibold text-gray-900">
                        {formatCurrencyFromCents(item.totalAmountInCents)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-3 flex justify-end">
                <div className="w-full max-w-xs space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-semibold text-gray-900">
                      {formatCurrencyFromCents(subtotal)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax</span>
                    <span className="font-semibold text-gray-900">
                      {formatCurrencyFromCents(tax)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-2">
                    <span className="font-bold text-gray-950">Total</span>
                    <span className="font-bold text-gray-950">
                      {formatCurrencyFromCents(total || amountDueInCents)}
                    </span>
                  </div>
                </div>
              </div>
            </>
          ) : null}

          <div className="mt-4 rounded border border-blue-200 p-3">
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-blue-600">
              Select Payment Method
            </p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5">
              {enabledMethods.length === 0 ? (
                <p className="col-span-full text-sm text-gray-500">
                  No manual payment methods are enabled in chamber settings.
                </p>
              ) : null}
              {enabledMethods.map((method) => {
                const isSelected = method.id === methodId;
                return (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => setMethodId(method.id)}
                    className={`rounded border p-2 text-center transition ${
                      isSelected
                        ? "border-blue-600 bg-blue-50 shadow-sm"
                        : "border-blue-300 bg-white hover:border-blue-500"
                    }`}
                  >
                    <span className="block text-sm font-bold text-gray-900">
                      {method.label}
                    </span>
                    <span className="mt-1 block rounded bg-blue-600 px-2 py-1 text-xs font-bold text-white">
                      Pay Now
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <p className="mb-1 text-xs font-bold uppercase text-gray-700">
                Payment Date
              </p>
              <TextField
                type="date"
                value={paymentDate}
                onChange={({ target }) => setPaymentDate(target.value)}
                fullWidth
              />
            </div>
            <div>
              <p className="mb-1 text-xs font-bold uppercase text-gray-700">
                Amount to Pay
              </p>
              {/* POL-005: shared CurrencyInput (no number-spinner arrows, no
                  'e', blur canonicalization; '$' is an adornment). The modal
                  skin reproduces the 56px MUI-outline look of the modal's
                  other fields (precedent: wizardCurrencyInputClassName). */}
              <CurrencyInput
                value={amount}
                onChange={setAmount}
                ariaLabel="Amount to pay"
                inputClassName={paymentModalCurrencyInputClassName}
                adornmentClassName="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-gray-500"
              />
            </div>
          </div>
          <div className="mt-3">
            <p className="mb-1 text-xs font-bold uppercase text-gray-700">
              Reference Number (Cheque # or Transaction ID)
            </p>
            <TextField
              value={reference}
              onChange={({ target }) => setReference(target.value)}
              fullWidth
              placeholder="Enter Reference number and details"
            />
          </div>
          <div className="mt-3">
            <p className="mb-1 text-xs font-bold uppercase text-gray-700">
              Internal Notes
            </p>
            <TextField
              value={note}
              onChange={({ target }) => setNote(target.value)}
              fullWidth
              multiline
              minRows={2}
              placeholder="Additional details for bookkeeping..."
            />
          </div>

          {/* Light-blue footer band across the full modal width (mock). */}
          <div className="-mx-6 -mb-5 mt-6 flex items-center gap-3 border-t border-blue-100 bg-blue-50 px-6 py-4">
            <Button
              fullWidth
              variant="contained"
              disabled={loading || !methodId || dollarsToCents(amount) <= 0}
              onClick={() => setConfirmOpen(true)}
              sx={{
                backgroundColor: "#2563eb",
                "&:hover": { backgroundColor: "#1d4ed8" },
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Confirm &amp; Apply Payment
            </Button>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                backgroundColor: "#ffffff",
                borderColor: "#111827",
                color: "#111827",
                textTransform: "none",
                fontWeight: 700,
                minWidth: 120,
                "&:hover": {
                  backgroundColor: "#f9fafb",
                  borderColor: "#000000",
                },
              }}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        tone="question"
        title="Are you sure you want to process payment for this invoice?"
        confirmLabel="Proceed"
        cancelLabel="Cancel"
        confirmTone="blue"
        loading={loading}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};
